import SCHEDULE from '../content/schedule.json';
import { toMinutes, fmtTime } from '../utils/time';

// Show full day window needed for Saturday morning Open Mat while keeping evenings compact
const HOURS_START = 10; // 10 AM
const HOURS_END = 21; // 9 PM (exclusive) => last visible half-hour is 8:30 PM
const HALF_STEPS = (HOURS_END - HOURS_START) * 2; // number of 30-min slots (10:00 AM inclusive to 9:00 PM exclusive)

// Helpers for placement and formatting
const RANGE_START_MIN = HOURS_START * 60;
const RANGE_END_MIN = HOURS_END * 60; // exclusive upper bound for rows
const clampToRange = (mins: number) => Math.max(RANGE_START_MIN, Math.min(RANGE_END_MIN, mins));
// Row slot index helpers: floor start, ceil end to avoid overflow past expected time
const slotFloorLocal = (t: string) => Math.floor((clampToRange(toMinutes(t)) - RANGE_START_MIN) / 30);
const slotCeilLocal = (t: string) => Math.ceil((clampToRange(toMinutes(t)) - RANGE_START_MIN) / 30);

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function Schedule() {
  // Compute which 30-min rows have at least one class across the week
  const used = Array.from({ length: HALF_STEPS }, () => false);
  DAYS.forEach((day) => {
    const events = (SCHEDULE as any)[day] || [];
    events.forEach((ev: any) => {
      const s = slotFloorLocal(ev.start);
      const e = slotCeilLocal(ev.end);
      for (let r = Math.max(0, s); r < Math.min(HALF_STEPS, e); r++) used[r] = true;
    });
  });
  const visibleRows = used.map((u, i) => (u ? i : -1)).filter((i) => i >= 0);

  return (
    <main id="main">
      <section className="section">
        <div className="container">
          <h1 className="section-title">Schedule</h1>
          <p className="fine">Times may vary on holidays or special events.</p>

          <div className="time-grid">
            {/* Header row: days across */}
            <div
              className="schedule-days"
              style={{ gridTemplateColumns: `140px repeat(${DAYS.length}, 1fr)` }}
            >
              <div className="corner-cell" />
              {DAYS.map((d) => (
                <div key={d} className="day-head">
                  {d}
                </div>
              ))}
            </div>

            {/* Body: hours down the side */}
            <div
              className="schedule-body"
              style={{
                gridTemplateColumns: `140px repeat(${DAYS.length}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${visibleRows.length}, minmax(56px, auto))`,
              }}
            >
              {visibleRows.map((r, idx) => {
                const totalMins = HOURS_START * 60 + r * 30;
                const hh = Math.floor(totalMins / 60);
                const mm = totalMins % 60;
                const label =
                  mm === 0 ? `${hh % 12 === 0 ? 12 : hh % 12}:00 ${hh < 12 ? 'AM' : 'PM'}` : '';
                return [
                  <div
                    key={`t-${r}`}
                    className="time-cell"
                    style={{ gridColumn: 1, gridRow: idx + 1 }}
                  >
                    {label}
                  </div>,
                  ...DAYS.map((_, di) => (
                    <div
                      key={`s-${r}-${di}`}
                      className="slot"
                      style={{ gridColumn: di + 2, gridRow: idx + 1 }}
                    />
                  )),
                ];
              })}

              {DAYS.map((day, di) => {
                const events = ((SCHEDULE as any)[day] || []) as any[];
                const inWindow = events.filter(
                  (ev) => toMinutes(ev.end) > RANGE_START_MIN && toMinutes(ev.start) < RANGE_END_MIN,
                );
                if (!inWindow.length && day === 'Sunday' && visibleRows.length) {
                  const col = di + 2;
                  // Render a small 'Closed' chip in each visible time row for consistency
                  return visibleRows.map((slot, idx) => (
                    <div
                      key={`${day}-closed-${idx}`}
                      className="event-closed"
                      style={{ gridColumn: `${col}`, gridRow: `${idx + 1}` }}
                    >
                      Closed
                    </div>
                  ));
                }
                return inWindow.map((ev, i) => {
                  const startSlot = slotFloorLocal(ev.start);
                  const endSlot = slotCeilLocal(ev.end);
                  const startIndex = visibleRows.indexOf(startSlot);
                  if (startIndex < 0) return null;
                  let j = startIndex;
                  while (j < visibleRows.length && visibleRows[j] < endSlot) j++;
                  const span = Math.max(1, j - startIndex);
                  const col = di + 2; // first col is time labels
                  return (
                    <div
                      key={`${day}-${i}`}
                      className="event"
                      style={{ gridColumn: `${col}`, gridRow: `${startIndex + 1} / span ${span}` }}
                    >
                      <strong>{ev.label}</strong>
                      <small className="time">
                        {fmtTime(ev.start)} - {fmtTime(ev.end)}
                      </small>
                    </div>
                  );
                });
              })}
            </div>
          </div>

          {/* Mobile-friendly list view */}
          <div className="schedule-mobile">
            {DAYS.map((day) => {
              const events = ((SCHEDULE as any)[day] || []).sort(
                (a: any, b: any) => toMinutes(a.start) - toMinutes(b.start),
              );
              return (
                <div key={day} className="day-card">
                  <h4 className="day-title">{day}</h4>
                  {events.length === 0 ? (
                    <p className="fine">Closed</p>
                  ) : (
                    <ul className="day-events">
                      {events.map((ev: any, i: number) => (
                        <li key={i} className="day-event">
                          <strong>{ev.label}</strong>
                          <span className="time">
                            {fmtTime(ev.start)} - {fmtTime(ev.end)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}

